import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import FilterBar from '@/components/FilterBar';
import TerraceMap from '@/components/TerraceMap';
import TerraceList from '@/components/TerraceList';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Map, List, Plus, Search } from 'lucide-react';
import { useTerraces } from '@/hooks/useTerraces';
import { useScreenSize } from '@/hooks/use-mobile';
import logoImage from '@/assets/followthesun-logo.png';

const Index = () => {
  const { isMobile, isTablet } = useScreenSize();
  const [showMap, setShowMap] = useState(!isMobile);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    openNow: false, // Show all terraces by default
    hasFood: false,
    petFriendly: false,
    servesAlcohol: false
  });

  const [userLocation, setUserLocation] = useState<{ lat: number, lng: number } | null>(null);
  const { terraces, loading, totalCount } = useTerraces(filters);
  
  // Filter terraces based on search term
  const filteredTerraces = useMemo(() => {
    if (!searchTerm.trim()) return terraces;
    
    const searchLower = searchTerm.toLowerCase();
    return terraces.filter(terrace => 
      terrace.name.toLowerCase().includes(searchLower) ||
      terrace.address.toLowerCase().includes(searchLower) ||
      terrace.type.toLowerCase().includes(searchLower) ||
      terrace.terraceDescription?.toLowerCase().includes(searchLower)
    );
  }, [terraces, searchTerm]);
  
  const sunnyTerraces = filteredTerraces.filter(t => t.sunStatus === 'sunny');

  React.useEffect(() => {
    setUserLocation({ lat: 59.3293, lng: 18.0686 });
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-warm-cream">
      <NavBar />
      
      <main className="flex-1 flex flex-col">
        <div className="w-full py-3 sm:py-4 md:py-6 space-y-3 sm:space-y-4 md:space-y-6">
          <div className="flex flex-col items-center text-center space-y-2 sm:space-y-3 px-4 sm:px-6">
            <div className="flex items-center space-x-2">
              <img src={logoImage} alt="FollowTheSun" className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 animate-glow" />
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gradient-sunset">
                {isMobile ? 'Follow The Sun' : 'Follow The Sun'}
              </h1>
            </div>
            <p className="text-sm sm:text-base text-muted-foreground max-w-[90%] sm:max-w-[600px]">
              {isMobile 
                ? 'Find sunny terraces in Stockholm right now.' 
                : 'Find cafes, bars, and restaurants with sun on their terraces right now in Stockholm.'
              }
            </p>
          </div>

          <div className="flex justify-center px-4 sm:px-6">
            <div className="bg-gradient-to-r from-sunset-yellow/40 to-peach-orange/30 rounded-lg p-3 sm:p-4 text-center shadow-sunset border border-peach-orange/20 w-full max-w-sm sm:max-w-none sm:w-auto">
              <p className="text-sm sm:text-base md:text-lg font-medium text-sunset-dark">
                {isMobile 
                  ? `${sunnyTerraces.length} sunny terraces now!`
                  : `${sunnyTerraces.length} sunny terraces right now in Stockholm!`
                }
              </p>
            </div>
          </div>

          <div className="flex justify-center px-4 sm:px-6">
            <div className="relative w-full max-w-sm sm:max-w-md rounded-2xl border border-white/30 bg-white/10 backdrop-blur-md p-4 shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-peach-orange/10 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-peach-orange">
              <Plus className="h-4 w-4" />
              <h5 className="mb-1 font-inter font-medium leading-none tracking-tighter text-sunset-dark">
                Don't see your favorite spot?
              </h5>
              <div className="text-sm text-sunset-medium mb-3">
                <p className="leading-relaxed">Help others discover sunny terraces by adding your favorite place to our map.</p>
              </div>
              <Button 
                asChild
                size="sm"
                className="w-full bg-gradient-to-r from-peach-orange to-coral-pink hover:from-coral-pink hover:to-peach-orange border-0 text-white font-medium shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Link to="/submit-terrace">
                  Submit a terrace
                </Link>
              </Button>
            </div>
          </div>

          <div className="flex gap-2 justify-center px-4 sm:px-6">
            <Button 
              variant={showMap ? "default" : "outline"} 
              onClick={() => setShowMap(true)}
              size="sm"
              className="flex items-center gap-2 min-w-[100px] h-10 sm:h-9 hover:bg-primary hover:text-primary-foreground"
            >
              <Map className="h-4 w-4" />
              <span>Map</span>
            </Button>
            <Button 
              variant={!showMap ? "default" : "outline"} 
              onClick={() => setShowMap(false)}
              size="sm"
              className="flex items-center gap-2 min-w-[100px] h-10 sm:h-9 hover:bg-primary hover:text-primary-foreground"
            >
              <List className="h-4 w-4" />
              <span>List</span>
            </Button>
          </div>

          <FilterBar filters={filters} setFilters={setFilters} />
          
          {/* Search Bar */}
          <div className="flex justify-center items-center gap-4 px-4 sm:px-6">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                type="text"
                placeholder="Search terraces..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-background/80 border-border/50 focus:border-peach-orange"
              />
            </div>
            <div className="bg-gradient-to-r from-dusty-rose/30 to-sunset-yellow/40 rounded-lg px-3 py-2 border border-dusty-rose/20 whitespace-nowrap">
              <p className="text-sm font-medium text-sunset-dark">
                {filteredTerraces.length} of {totalCount} total
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 px-3 sm:px-4 md:px-6 pb-6 sm:pb-8">
          {showMap ? (
            <div className="w-full h-full min-h-[200px] max-h-[500px]">
              <TerraceMap terraces={filteredTerraces} userLocation={userLocation} />
            </div>
          ) : (
            <TerraceList terraces={filteredTerraces} loading={loading} />
          )}
        </div>
      </main>

      <footer className="bg-gradient-to-r from-sunset-yellow/20 to-dusty-rose/20 border-t border-peach-orange/20 py-3 sm:py-4 md:py-6 mt-auto">
        <div className="w-full px-4 sm:px-6 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
          <p className="text-xs sm:text-sm text-sunset-medium text-center sm:text-left">
            © 2025 FollowTheSun. All rights reserved.
          </p>
          <div>
            <Button 
              variant="link" 
              className="text-xs sm:text-sm p-2 sm:p-4 h-auto" 
              asChild
            >
              <Link to="/submit-terrace">Submit a terrace</Link>
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
